import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'
import type {
  ChangeEvent,
  ChangeEventHandler,
  PropsWithChildren,
  Ref,
} from 'react'
import { useSyncRefs } from '../hooks/useSyncRefs'

interface SwitchProps {
  /**
   * Accessible label for the switch input element
   */
  'aria-label'?: string
  /**
   * The color scheme of the switch
   * @default 'primary'
   */
  color?: SwitchColor
  /**
   * Initial checked state of the switch
   * @default false
   */
  defaultChecked?: boolean
  /**
   * If true, the switch is be disabled
   * @default false
   */
  disabled?: boolean
  /**
   * Additional descriptive text to display below the switch label
   */
  helperText?: string
  /**
   * Reference to the underlying input element
   */
  inputRef?: Ref<HTMLInputElement>
  /**
   * The name attribute for the input element
   */
  name?: string
  /**
   * Callback fired when the state is changed
   */
  onChange?: ChangeEventHandler<HTMLInputElement>
  /**
   * The size of the switch
   * @default 'small'
   */
  size?: SwitchSize
}

type SwitchColor = 'primary' | 'secondary'

type SwitchColors = {
  [key in SwitchColor]: {
    background: {
      focused: string
      unfocused: string
    }
    track: {
      disabled: string
      focused: string
      unfocused: string
    }
  }
}

type SwitchSize = 'medium' | 'small'

type SwitchSizes = {
  [key in SwitchSize]: {
    root: string
    thumb: string
    transform: string
  }
}

const colors: SwitchColors = {
  primary: {
    background: {
      focused: `bg-primary-10`,
      unfocused: `group-hover:bg-primary-10 group-active:bg-primary-20`,
    },
    track: {
      disabled: `bg-primary-20`,
      focused: `bg-primary-dark`,
      unfocused: `bg-primary-main hover:bg-primary-dark active:bg-primary-darker`,
    },
  },
  secondary: {
    background: {
      focused: `bg-secondary-10`,
      unfocused: `group-hover:bg-secondary-10 group-active:bg-secondary-20`,
    },
    track: {
      disabled: `bg-secondary-20`,
      focused: `bg-secondary-dark`,
      unfocused: `bg-secondary-main hover:bg-secondary-dark active:bg-secondary-darker`,
    },
  },
}

const sizes: SwitchSizes = {
  medium: {
    root: 'w-11 h-6',
    thumb: 'w-5 h-5',
    transform: 'translate-x-5',
  },
  small: {
    root: 'w-9 h-5',
    thumb: 'w-4 h-4',
    transform: 'translate-x-4',
  },
}

function Switch({
  'aria-label': label,
  children = null,
  color = 'primary',
  defaultChecked = false,
  disabled = false,
  helperText,
  inputRef,
  name,
  onChange,
  size = 'small',
}: PropsWithChildren<SwitchProps>): JSX.Element {
  const ref = useRef<HTMLInputElement | null>(null)
  const [checked, setChecked] = useState(() => defaultChecked)
  const [focused, setFocused] = useState(() => false)
  const switchRef = useSyncRefs(ref, inputRef)

  const handleBlur = () => {
    setFocused(false)
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setChecked(event.currentTarget.checked)

    if (onChange) {
      onChange(event)
    }
  }

  const handleFocus = () => {
    setFocused(true)
  }

  useEffect(() => {
    if (ref.current) {
      setChecked(ref.current.checked)
    }
  }, [])

  return (
    <label
      className={clsx(
        'inline-flex items-center font-sans group',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      )}
    >
      <div className="flex flex-col">
        <div className="inline-flex items-center">
          <div
            className={clsx(
              'relative inline-flex transition-colors duration-200 border-2 border-transparent rounded-xl',
              checked
                ? disabled
                  ? colors[color].track.disabled
                  : colors[color].track.unfocused
                : disabled
                ? null
                : 'bg-black-45 hover:bg-black-60 focus:bg-black-60 active:bg-black-90',
              checked && focused ? colors[color].track.focused : null,
              !checked && focused ? 'bg-black-60' : null,
              disabled ? 'bg-black-15' : null,
              sizes[size].root,
            )}
          >
            <span
              aria-hidden="true"
              className={clsx(
                'relative transition duration-200 ease-in-out rounded-full pointer-events-none will-change-transform',
                checked ? sizes[size].transform : null,
                sizes[size].thumb,
              )}
            >
              <span
                className={clsx(
                  'absolute inset-0 rounded-full scale-200',
                  focused ? colors[color].background.focused : null,
                  disabled ? null : colors[color].background.unfocused,
                )}
              >
                <span className="absolute inset-0 scale-50 rounded-full bg-white-100" />
              </span>
            </span>
            <input
              aria-label={label}
              className={clsx(
                'absolute inset-0 w-full h-full p-0 m-0 opacity-0',
                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
              defaultChecked={defaultChecked}
              disabled={disabled}
              name={name}
              onBlur={handleBlur}
              onChange={handleChange}
              onFocus={handleFocus}
              ref={switchRef}
              type="checkbox"
            />
          </div>
          {children ? (
            <p
              className={clsx(
                'ml-2 text-sm font-normal leading-5 select-none',
                disabled ? 'text-black-45' : 'text-black-90',
              )}
            >
              {children}
            </p>
          ) : null}
        </div>
        {children ? (
          <div
            className={clsx(
              'text-xs font-normal tracking-wid pt-2',
              disabled ? 'text-black-30' : 'text-black-60',
            )}
          >
            {helperText ? helperText : null}
          </div>
        ) : null}
      </div>
    </label>
  )
}

export default Switch
